import { EmptyState, Spinner } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { Waypoint } from 'react-waypoint';

import { auditLogsApi } from 'apis/auditLogs';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { Writable } from 'ts-essentials';
import styles from './activityLog.module.scss';
import { ComponentByLogType } from './activityTypes';
import { ActivityLogFilters } from './filters';
import { ActivityLogFiltersValue } from './filters/activityLogFilters.types';

export const isDateRangeValid = (fromTs?: string, toTs?: string): boolean => {
  if (fromTs && toTs && moment(fromTs).isAfter(moment(toTs))) {
    return false;
  }

  return true;
};

/** Normalizes a search param that may be a single value into an array. */
function toArray<T>(value?: T | readonly T[]): T[] {
  return value === undefined
    ? []
    : Array.isArray(value)
      ? [...value]
      : [value as T];
}

/**
 * Represents a component that outputs the log entries for the main
 * conversation.
 */
export function ActivityLog(): JSX.Element {
  const {
    metaConversation: { conversation, messages },
  } = useAppSelector(
    (state) => ({
      metaConversation: state.conversation.metaConversation,
    }),
    shallowEqual,
  );

  const { params, patchParams, unsetParams } = useAppSearchParams(
    '/conversation/:csid/activity-log',
  );

  const filters = useMemo<ActivityLogFiltersValue>(() => {
    const eventTypes = toArray(params.eventType);
    const authorIds = toArray(params.author);

    return {
      fromTs: params.from,
      toTs: params.to,
      eventTypes: eventTypes.length ? eventTypes : undefined,
      authorIds: authorIds.length ? authorIds : undefined,
    };
  }, [params.from, params.to, params.eventType, params.author]);

  const {
    items: entries,
    changeArg,
    asyncStatus,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: auditLogsApi.endpoints.readConversationAuditLogs,
    itemsSelector: (response) => response.data.logs,
    infinite: true,
  });

  const handleFiltersChange = useCallback(
    (changed: Partial<ActivityLogFiltersValue>) => {
      const set: Writable<typeof params> = {};
      const unset: (keyof typeof params)[] = [];

      if ('fromTs' in changed) {
        if (changed.fromTs) set.from = changed.fromTs;
        else unset.push('from');
      }
      if ('toTs' in changed) {
        if (changed.toTs) set.to = changed.toTs;
        else unset.push('to');
      }
      if ('eventTypes' in changed) {
        if (changed.eventTypes?.length) set.eventType = changed.eventTypes;
        else unset.push('eventType');
      }
      if ('authorIds' in changed) {
        if (changed.authorIds?.length) set.author = changed.authorIds;
        else unset.push('author');
      }

      patchParams({ set, unset });
    },
    [patchParams],
  );

  const handleClearFilters = useCallback(
    () => unsetParams(['from', 'to', 'eventType', 'author']),
    [unsetParams],
  );

  const hasActiveFilters = Boolean(
    filters.fromTs ||
    filters.toTs ||
    filters.eventTypes?.length ||
    filters.authorIds?.length,
  );

  const isDateRangeInvalid = !isDateRangeValid(filters.fromTs, filters.toTs);

  // Rendered in every empty state so an invalid or unmatched filter is always
  // recoverable - even when the filters toolbar is hidden by the feature flag.
  const clearFiltersButtonProps = hasActiveFilters
    ? {
        children: 'Clear filters',
        variant: 'secondary' as const,
        onClick: handleClearFilters,
      }
    : undefined;

  useEffect(() => {
    // Skip the request while the date range is invalid so we don't query with a nonsensical filter.
    if (conversation?.id && isDateRangeValid(filters.fromTs, filters.toTs)) {
      changeArg({
        resourceId: conversation.id.toString(),
        limit: 1000,
        sort: { key: 'timestamp', direction: 'asc' },
        // Pass every filter explicitly (even when undefined) so cleared filters
        // overwrite the previous arg rather than being merged away.
        fromTs: filters.fromTs,
        toTs: filters.toTs,
        eventTypes: filters.eventTypes,
        authorIds: filters.authorIds,
      });
    }
  }, [changeArg, conversation.id, filters]);

  return (
    <>
      <ActivityLogFilters filters={filters} onChange={handleFiltersChange} />
      <div
        className={classnames({
          [styles.root]: true,
          [styles.hasLogs]: entries.length,
        })}
      >
        {isDateRangeInvalid ? (
          <EmptyState
            icon="calendar-times"
            title="Invalid date range"
            description="The start date is after the end date. Adjust the range to see the activity log."
            buttonProps={clearFiltersButtonProps}
          />
        ) : (
          <>
            {asyncStatus === 'rejected' && (
              <span className={styles.error}>
                Failed to retrieve Activity Log.
              </span>
            )}

            {entries.map((entry) => (
              <ComponentByLogType
                key={entry.event_id}
                entry={entry}
                messages={messages}
              />
            ))}
            {asyncStatus === 'fulfilled' && !entries.length && (
              <EmptyState
                icon="file-text-search"
                title="No logs found"
                description={
                  hasActiveFilters
                    ? 'No activity matches the selected filters. Try clearing them to see more.'
                    : 'There are no events to display for this conversation.'
                }
                buttonProps={clearFiltersButtonProps}
              />
            )}

            {(asyncStatus === 'pending' || asyncStatus === 'uninitialized') && (
              <div className={styles.spinnerContainer} data-testid="spinner">
                <Spinner />
              </div>
            )}

            {asyncStatus === 'fulfilled' && hasNextPage && (
              <Waypoint onEnter={nextPage} />
            )}
          </>
        )}
      </div>
    </>
  );
}
